import { CdkMenuTrigger } from '@angular/cdk/menu';
import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { Approval } from 'src/app/models/document';
import { MessageService } from 'src/app/services/message.service';

type ApprovalOption = { selected: boolean, disabled?: boolean, display?: boolean, value: Approval }

@Component({
  selector: 'app-document-approval-select',
  templateUrl: './document-approval-select.component.html',
  styleUrls: ['./document-approval-select.component.scss']
})
export class DocumentApprovalSelectComponent {
  private id: string;
  @Input() label?: string;
  @Input() labelOutside: boolean = false;
  @Input() multiple: boolean = false;

  @Input() disabled: boolean = false;
  @Input() disabledValues: Approval[] = [];
  @Input() emitOnInit: boolean = true;
  @Input() restrict: Approval[] = [];
  @Input() noAutomatic: boolean = false;

  classes: ApprovalOption[] = [];

  selected: ApprovalOption[] = [];

  _filter?: string;

  @Output() change = new EventEmitter<any>();
  @Input() value?: Approval | Approval[];
  @Output() valueChange = new EventEmitter<any>();

  show: boolean = false;

  selection?: string;

  subscription: Subscription;

  @ViewChild('trigger') trigger?: CdkMenuTrigger;

  constructor(
    private _message: MessageService,
    private translate: TranslateService
  ) {
    this.id = _.uniqueId("SELECT-");
    this.subscription = this._message.changeEmitted$.subscribe(change => {
      if (change && change.key == "LAYOUT.SELECT") {
        (change.data.id !== this.id) && (this.show = false);
      }
    });
  }

  ngOnInit(): void {
    this.setInitialState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['restrict'] && !_.isEqual(changes['restrict']['currentValue'], changes['restrict']['previousValue'])) {
      this.retrieveApprovals();
    }
  }

  async retrieveApprovals() {
    const classes = Object.keys(Approval).map(key => {
      const value = Approval[key as keyof typeof Approval];
      return {
        value: Approval[key as keyof typeof Approval],
        selected: this._isSelected(value),
        disabled: this._isDisabled(value)
      }
    })
    if (this.restrict && this.restrict.length > 0) {
      this.classes = classes.filter((docClass) => this.restrict.includes(docClass.value))
    } else {
      this.classes = classes;
    }
  }

  _isDisabled(option: Approval) {
    return this.disabledValues.includes(option);
  }

  _isSelected(option: Approval) {
    return this.selected.map((d) => d.value).includes(option);
  }

  _documentClassOptionComparator(optionA: ApprovalOption, optionB: ApprovalOption) {
    return optionA.value === optionB.value;
  }

  populate() {
    if (this.multiple && this.value && (this.value as Approval[]).length > 0) {
      const values = (this.value as Approval[]);
      this.selected = values.map((value) => ({ value, selected: true, disabled: false, display: true }));
    } else if (!this.multiple && this.value) {
      const value = (this.value as Approval);
      this.selected = [{ value, selected: true, disabled: false, display: true }];
    }
  }

  selectedToDisplay() {
    return this.selected.filter((o) => o.display);
  }

  isAllSelected() {
    return this.classes.every((docClass) => docClass.selected);
  }

  toggleAllSelection() {
    if (!this.isAllSelected()) {
      this.classes.forEach((docClass) => {
        docClass.selected = true;
      })
    } else {
      this.classes.forEach((docClass) => {
        docClass.selected = false;
      })
    }
    this.onChange();
  }

  async setInitialState() {
    await this.retrieveApprovals();
    if (this.multiple && this.value && (this.value as Approval[]).length > 0) {
      const values = (this.value as Approval[]);
      this.selected = values.map((value) => ({ value, selected: true, disabled: false }));
      this.classes.forEach(docClass => {
        if (values.includes(docClass.value)) {
          docClass.selected = true;
        } else {
          docClass.selected = false;
        }
      });
      const value = _.unionWith([...this.classes.filter(c => c.selected), ...this.selected], this._documentClassOptionComparator);
      if (value.length > 0) {
        this.selection = value.length === 1 ? this.translate.instant(`DOCUMENT.APPROVAL_${value[0].value.toUpperCase()}`) : `${this.label} (${value.length})`;
      } else {
        this.selection = undefined;
      }
    } else if (this.value) {
      const value = this.classes.find(c => c.value === this.value);
      if (value) {
        value.selected = true;
      }
      this.selection = value ? this.translate.instant(`DOCUMENT.APPROVAL_${value.value.toUpperCase()}`) : undefined;
    } else {
      this.classes.forEach(docClass => {
        docClass.selected = false;
      });
    }
    if (this.emitOnInit) {
      this.onChange();
    }

  }

  removeFromPreselection(option: ApprovalOption) {
    this.selected.splice(this.selected.findIndex((o) => option.value === o.value), 1);
    const doc = this.classes.find((t) => t.value === option.value);
    if (doc) {
      doc.selected = false;
    }
    this.onChange();
  }

  toggleFromSelection(option: ApprovalOption) {
    if (!option.selected) {
      this.selected.splice(this.selected.findIndex((o) => option.value === o.value), 1);
    } else if (!this._isSelected(option.value)) {
      this.selected.push({ ...option, display: false });
    }
  }

  select(option: any) {
    option.selected = option.selected !== null ? !option.selected : true;
    this.toggleFromSelection(option)
    if (!this.multiple) {
      this.classes.forEach(c => {
        if (c.value !== option.value) {
          c.selected = false;
        }
      })
    }
    this.onChange();
  }

  onChange() {
    if (!this.multiple) {
      const value = this.classes.find(c => c.selected) !== undefined ? this.classes.find(c => c.selected) : (this.selected.length === 1 ? this.selected[0] : undefined);
      this.selection = value ? this.translate.instant(`DOCUMENT.APPROVAL_${value.value.toUpperCase()}`) : undefined;
      this.valueChange.emit(value?.value as Approval);
      this.change.emit({ value });
      this.trigger?.close();
      this.value = value?.value;

    } else {
      const values = _.unionWith([...this.classes.filter(c => c.selected), ...this.selected], this._documentClassOptionComparator);
      this.value = values.map(c => c.value) as Approval[];
      if (values.length > 0) {
        this.selection = values.length === 1 ? this.translate.instant(`DOCUMENT.APPROVAL_${values[0].value.toUpperCase()}`) : `${this.label} (${values.length})`;
      } else {
        this.selection = undefined;
      }
      this.valueChange.emit(values.map(c => c.value) as Approval[]);
      this.change.emit({ value: values });
    }

  }


  toggle() {
    this.show = !this.show;
    if (this.show) {
      this._message.emitChange("LAYOUT", "SELECT", {
        id: this.id
      });
    }
  }

  filtered() {
    return (this._filter && this._filter.length > 0) ? this.classes.filter(docClass => {
      return docClass.value.toString().includes(this._filter as string) || this.translate.instant(`DOCUMENT.APPROVAL_${docClass.value.toUpperCase()}`).toLowerCase().includes(this._filter?.toLowerCase())
    }) : this.classes;
  }

  onSearch(event: any) {
    if (event && event.hasOwnProperty('value')) {
      this._filter = event.value;
    }
  }

  onClear() {
    if (this.multiple) {
      this.value = [];
    } else {
      this.value = undefined;
    }
    this.classes.forEach(docClass => {
      docClass.selected = false;
    });
    this.selected = [];
    this.selection = undefined
    this._filter = undefined;
    this.onChange();
    this.trigger?.close();
  }
}
